import { useState, useEffect } from 'react';
import { Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Dashboard from '../pages/Dashboard';
import TicketList from '../pages/TicketList';
import TicketDetail from '../pages/TicketDetail';
import StaffPage from '../pages/Staff';
import Account from '../pages/Account';
import Templates from '../pages/Templates';
import NewTicket from '../pages/NewTicket';
import Rules from '../pages/Rules';

const navStyle = (isActive) => ({
  display: 'flex', alignItems: 'center', gap: '8px',
  padding: '8px 14px', borderRadius: '7px', fontSize: '13px',
  textDecoration: 'none', color: isActive ? '#185FA5' : '#555',
  background: isActive ? '#E6F1FB' : 'transparent', fontWeight: isActive ? '500' : '400',
  marginBottom: '2px',
});

export default function Layout() {
  const { staff, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const location = useLocation();

  // Track screen size
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar on navigation (mobile)
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location]);

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f7f7f7' }}>

      {/* Mobile backdrop */}
      {isMobile && sidebarOpen && (
        <div
          onClick={closeSidebar}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
            zIndex: 99, backdropFilter: 'blur(1px)',
          }}
        />
      )}

      {/* Sidebar */}
      <div style={{
        width: '210px',
        background: '#fff',
        borderRight: '1px solid #e8e8e8',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: 0,
        zIndex: 100,
        transform: isMobile && !sidebarOpen ? 'translateX(-100%)' : 'translateX(0)',
        transition: 'transform 0.25s ease',
      }}>
        <div style={{ padding: '18px 16px 14px', borderBottom: '1px solid #f0f0f0' }}>
          <h1 style={{ fontSize: '15px', fontWeight: '600', color: '#111' }}>ClinicDesk</h1>
          <p style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>Patient support</p>
        </div>

        <nav style={{ padding: '10px 10px', flex: 1, overflowY: 'auto' }}>
          <NavLink to="/" end style={({ isActive }) => navStyle(isActive)}>Dashboard</NavLink>
          <NavLink to="/tickets" style={({ isActive }) => navStyle(isActive)}>All tickets</NavLink>
          <NavLink to="/tickets?assigned=me" style={({ isActive }) => navStyle(isActive)}>My tickets</NavLink>
          <NavLink to="/tickets?status=open&assigned=unassigned" style={({ isActive }) => navStyle(isActive)}>Unassigned</NavLink>
          <div style={{ height: '1px', background: '#f0f0f0', margin: '8px 4px' }} />
          <NavLink to="/tickets?status=open" style={({ isActive }) => navStyle(isActive)}>Open</NavLink>
          <NavLink to="/tickets?status=pending" style={({ isActive }) => navStyle(isActive)}>Pending</NavLink>
          <NavLink to="/tickets?status=resolved" style={({ isActive }) => navStyle(isActive)}>Resolved</NavLink>
          {staff?.role === 'admin' && (
            <>
              <div style={{ height: '1px', background: '#f0f0f0', margin: '8px 4px' }} />
              <NavLink to="/staff" style={({ isActive }) => navStyle(isActive)}>Staff members</NavLink>
              <NavLink to="/templates" style={({ isActive }) => navStyle(isActive)}>Templates</NavLink>
              <NavLink to="/rules" style={({ isActive }) => navStyle(isActive)}>Filter rules</NavLink>
            </>
          )}
        </nav>

        <div style={{ padding: '12px 14px', borderTop: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#CECBF6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '600', color: '#3C3489', flexShrink: 0 }}>
              {staff?.name?.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <p style={{ fontSize: '12px', fontWeight: '500' }}>{staff?.name}</p>
              <p style={{ fontSize: '11px', color: '#aaa' }}>{staff?.role}</p>
            </div>
          </div>
          <button onClick={() => window.location.href="/account"} style={{ width: '100%', padding: '6px', background: 'none', border: '1px solid #e8e8e8', borderRadius: '6px', fontSize: '12px', color: '#888', cursor: 'pointer' }}>My account</button>
          <button onClick={logout} style={{ width: '100%', padding: '6px', background: 'none', border: '1px solid #e8e8e8', borderRadius: '6px', fontSize: '12px', color: '#c00', cursor: 'pointer', marginTop: '4px' }}>Sign out</button>
        </div>
      </div>

      {/* Mobile top bar */}
      {isMobile && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, height: '52px',
          background: '#fff', borderBottom: '1px solid #e8e8e8',
          display: 'flex', alignItems: 'center', padding: '0 16px',
          zIndex: 98, gap: '12px',
        }}>
          <button
            onClick={() => setSidebarOpen(o => !o)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', flexDirection: 'column', gap: '5px' }}
            aria-label="Open menu"
          >
            <span style={{ display: 'block', width: '22px', height: '2px', background: '#333', borderRadius: '2px', transition: 'all 0.2s', transform: sidebarOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none' }} />
            <span style={{ display: 'block', width: '22px', height: '2px', background: '#333', borderRadius: '2px', opacity: sidebarOpen ? 0 : 1, transition: 'all 0.2s' }} />
            <span style={{ display: 'block', width: '22px', height: '2px', background: '#333', borderRadius: '2px', transition: 'all 0.2s', transform: sidebarOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none' }} />
          </button>
          <h1 style={{ fontSize: '15px', fontWeight: '600', color: '#111' }}>ClinicDesk</h1>
        </div>
      )}

      {/* Main content */}
      <div style={{
        marginLeft: isMobile ? '0' : '210px',
        flex: 1,
        padding: isMobile ? '68px 12px 16px' : '24px',
        minWidth: 0,
      }}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/tickets" element={<TicketList />} />
          <Route path="/tickets/:id" element={<TicketDetail />} />
          <Route path="/staff" element={<StaffPage />} />
          <Route path="/account" element={<Account />} />
          <Route path="/templates" element={<Templates />} />
          <Route path="/rules" element={<Rules />} />
          <Route path="/tickets/new" element={<NewTicket />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </div>
    </div>
  );
}
